import { receiveBoolean, receiveNumber, receiveString } from "../../../../controllers/adapters/receives"

/**
 * Formatea `2026-10-05` -> `05/10/2026`. Si el valor ya es texto
 * (`5 d'octubre de 2026`) se devuelve tal cual.
 */
const formatoFecha = (valor: any) => {

    const texto = receiveString(valor)

    if (!texto) return ''

    if (texto.includes(' ')) return texto

    const [anio, mes, dia] = texto.split('-')

    return anio && mes && dia ? `${dia}/${mes}/${anio}` : texto
}

/**
 * Mapea una fila del WS (prefijo `ac_*`) a la estructura interna de actividad.
 */
const schemaActividad = (item: any = {}) => {

    const centro = receiveString(item.ac_lugar_centro)
    const poblacion = receiveString(item.ac_lugar_poblacion)
    const direccion = receiveString(item.ac_lugar_direccion)
    const cp = receiveString(item.ac_lugar_cp)
    const espacio = receiveString(item.ac_espacio)

    const lugar = (centro || poblacion)
        ? (centro && poblacion ? `${centro}, ${poblacion}` : (centro || poblacion))
        : espacio

    const gratuita = receiveString(item.ac_esgratuita) === '1'

    return {
        id: receiveString(item.ac_id_actividad),
        titulo: receiveString(item.ac_actividad),
        fecha: receiveString(item.ac_fechas) || formatoFecha(item.ac_finicio),
        lugar,
        direccion,
        poblacion,
        cp,
        espacio,
        tipo: gratuita ? 'Gratuïta' : 'De pagament',
        gratuita,
        horario: receiveString(item.ac_horario),
        duracion: receiveString(item.ac_duracion),
        numPlazas: receiveString(item.ac_numeroplazas),
        precioInternet: receiveString(item.ac_precio_internet),
        precioColInternet: receiveString(item.ac_precio_col_internet),
        precioPrecolInternet: receiveString(item.ac_precio_precol_internet),
        enlace: receiveString(item.ac_enlaceweb),
        resumen: receiveString(item.ac_resumen),
        programa: receiveString(item.ac_programa),
        objetivos: receiveString(item.ac_objetivos),
        destinatarios: receiveString(item.ac_destinatarios),
        profesorado: receiveString(item.ac_profesorado),
        observaciones: receiveString(item.ac_observacionespublico),
        organizador: receiveString(item.ac_organizador),
    }
}

/**
 * Extrae el listado de la respuesta del WS. En actividades la lista está en
 * `data.data.data` (data -> paginación/objeto -> array); en noticias el array
 * está directamente en `data`. Se toleran ambas formas.
 */
const extraerLista = (data: any): any[] => {

    if (!data) return []

    const esActividad = (item: any) => item && typeof item === 'object' && item.ac_id_actividad

    // Respuesta del listado de actividades: { data: { data: [...], total, ... } }
    if (data?.data && typeof data.data === 'object' && !Array.isArray(data.data) && Array.isArray(data.data?.data)) {

        return data.data.data.filter(esActividad)
    }

    // { data: [...] }
    if (Array.isArray(data?.data)) return data.data.filter(esActividad)

    if (Array.isArray(data)) return data.filter(esActividad)

    return []
}

/**
 * Detecta si un objeto es una fila de actividad del WS (tiene campos `ac_*`).
 */
const esFilaActividad = (obj: any): boolean =>
    !!obj &&
    typeof obj === 'object' &&
    !Array.isArray(obj) &&
    Object.keys(obj).some((clave) => clave.startsWith('ac_'))

/**
 * Recupera la primera actividad de la respuesta del WS (para el detalle).
 * Formas esperadas:
 *   { data: { actividad: {...}, inscritos: [...] } }    (actual del WS)
 *   { data: { ...campos ac_*..., inscritos: [...] } }   (actividad plana)
 *   { actividad: {...} } | { data: [...] } | [...]      (formas toleradas)
 */
const extraerPrimera = (data: any): any => {

    if (!data) return {}

    if (Array.isArray(data)) return data.find(esFilaActividad) || {}

    if (esFilaActividad(data.data?.actividad)) return data.data.actividad

    if (esFilaActividad(data.data)) return data.data

    if (esFilaActividad(data.actividad)) return data.actividad

    for (const clave of ['data', 'actividad', 'result', 'items']) {

        if (Array.isArray(data?.[clave])) {

            const fila = data[clave].find(esFilaActividad)

            if (fila) return fila
        }
    }

    return esFilaActividad(data) ? data : {}
}

/**
 * Mapea una fila de inscrito (prefijo `in_*`) a la estructura interna.
 */
const schemaInscrito = (item: any = {}) => ({

    id: receiveString(item.in_id_inscripcion),
    nombre: receiveString(item.nombre_completo),
    nif: receiveString(item.cli_nif_cif),
    precio: receiveString(item.in_precio),
    in_estado: receiveString(item.in_estado),
    estado: receiveString(item.estado),
    f_asistencia: receiveString(item.in_f_asistencia),
})

const ActividadesAdapters = {

    // GET {base}/integraWeb.php?accion=obtener_actividades_disponibles&actividad=...&page=N
    // La paginación viene en el objeto `data` de la respuesta (total, per_page, current_page, last_page).
    GET_ACTIVIDADES: {

        send: {

            schema: function (item: any = {}) {

                return {
                    accion: item.accion || 'obtener_actividades_disponibles',
                    actividad: item.actividad || '',
                    page: item.page || 1,
                    ...item,
                }
            },

            adapter: function (data: any) {

                const item = this.schema(data)

                return {
                    ...item
                }
            }
        },

        receive: {

            schema: schemaActividad,

            adapter: function (data: any) {

                const items = extraerLista(data).map((item: any) => this.schema(item))

                const paginacion = data?.data || data || {}

                return {
                    items,
                    paginacion: {
                        total: receiveNumber(paginacion.total),
                        per_page: receiveNumber(paginacion.per_page),
                        page: receiveNumber(paginacion.current_page),
                        last_page: receiveNumber(paginacion.last_page),
                    }
                }
            }
        },
    },

    // GET {base}/integraWeb.php?accion=obtener_detalle_actividad&id=N
    // Se usa en el modal de previsualización de una actividad.
    GET_ACTIVIDAD: {

        send: {

            schema: function (item: any = {}) {

                return {
                    accion: item.accion || 'obtener_detalle_actividad',
                    id: item.id ?? item.ac_id_actividad,
                    ...item,
                }
            },

            adapter: function (data: any) {

                const item = this.schema(data)

                return {
                    ...item
                }
            }
        },

        receive: {

            schema: schemaActividad,

            adapter: function (data: any) {

                const raw = extraerPrimera(data)

                const actividad = this.schema(raw)

                const inscritos = Array.isArray(data?.data?.inscritos)
                    ? data.data.inscritos.map((item: any) => schemaInscrito(item))
                    : []

                return {
                    data: {
                        ...actividad,
                        estado: receiveString(raw.estado),
                        tipoTxt: receiveString(raw.tipo),
                        tarifas: Array.isArray(raw.tarifas_lista) ? raw.tarifas_lista : [],
                        ivaInfo: raw.iva_info != null ? receiveString(raw.iva_info) : '',
                        fechaLimitePago: raw.fecha_limite_pago != null ? receiveString(raw.fecha_limite_pago) : '',
                        plazasTotales: raw.plazas_totales != null ? receiveString(raw.plazas_totales) : '',
                        plazasDisponibles: raw.plazas_disponibles != null ? receiveNumber(raw.plazas_disponibles) : 0,
                        finicio: receiveString(raw.ac_finicio),
                        ffin: receiveString(raw.ac_ffin),
                        espacio: receiveString(raw.ac_espacio),
                        telefono: receiveString(raw.ac_lugar_telefono),
                        mail: receiveString(raw.ac_lugar_mail),
                        web: receiveString(raw.ac_lugar_web),
                        derechosinsc: receiveString(raw.ac_derechosinsc),
                        documentacion: receiveString(raw.ac_documentacion),
                    },
                    inscritos,
                }
            }
        },
    },

    // POST {base}/integraWeb.php  accion=inscribir_personas_actividad
    // Inscribe un array de personas en una actividad. El WS (adminactivitats) lee el
    // cuerpo JSON (php://input). `personas` es un array con el objeto
    // `persona_fisica` completo de cada persona (el mismo que devuelve
    // `/empresas/personas/disponibles`), no solo el NIF: el WS necesita el resto de
    // campos para dar de alta o actualizar el `clientes` de la persona, igual que
    // hace con `empresa`. El NIF viaja dentro de ese objeto.
    POST_INSCRIBIR_PERSONAS: {

        send: {

            schema: function (item: any = {}) {

                return {
                    accion: item.accion || 'inscribir_personas_actividad',
                    actividad_id: item.ac_id_actividad || '',
                    personas: Array.isArray(item.personas) ? item.personas : [],
                    ...item,
                }
            },

            adapter: function (data: any) {

                const item = this.schema(data)

                return {
                    ...item
                }
            }
        },

        receive: {

            schema: function (item: any = {}) {

                return {
                    success: receiveBoolean(item?.success),
                    message: receiveString(item?.message),
                }
            },

            adapter: function (data: any) {

                return this.schema(data?.data ?? data)
            }
        },
    },

    // POST {base}/integraWeb.php  accion=anular_inscripcion
    // Anula una inscripción del WS. `id` = id de la inscripción (in_id_inscripcion).
    // El WS lee el cuerpo JSON (php://input), igual que inscribir_personas_actividad.
    ANULAR_INSCRIPCION: {

        send: {

            schema: function (item: any = {}) {

                return {
                    accion: item.accion || 'anular_inscripcion',
                    id: item.id ?? item.in_id_inscripcion,
                    ...item,
                }
            },

            adapter: function (data: any) {

                const item = this.schema(data)

                return {
                    ...item
                }
            }
        },

        receive: {

            schema: function (item: any = {}) {

                return {
                    success: receiveBoolean(item?.success),
                    message: receiveString(item?.message),
                }
            },

            adapter: function (data: any) {

                return this.schema(data?.data ?? data)
            }
        },
    },
}

export {
    ActividadesAdapters
}